/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Remove "MRP:" text from price--highlight when price--compare is present
 * in the same price-list container. Sets data-price attribute for CSS ::after.
 */
(function () {
  function setMRPFreePrice() {
    // Find all price-list containers that have both price--highlight and price--compare
    const priceLists = document.querySelectorAll('.price-list');

    priceLists.forEach(function (priceList) {
      const hasCompare = priceList.querySelector('.price--compare');
      const highlightPrice = priceList.querySelector('.price--highlight');

      if (hasCompare && highlightPrice && !highlightPrice.hasAttribute('data-price')) {
        // Extract price text without "MRP:" prefix
        let priceText = '';
        highlightPrice.childNodes.forEach(function (node) {
          if (node.nodeType === Node.TEXT_NODE) {
            priceText += node.textContent.replace('MRP:', '').trim();
          }
        });

        // Set data-price attribute for CSS ::after to use
        highlightPrice.setAttribute('data-price', priceText);
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setMRPFreePrice);
  } else {
    setMRPFreePrice();
  }

  // Also observe for dynamic content changes (e.g., variant changes)
  const observer = new MutationObserver(function (mutations) {
    setMRPFreePrice();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

/**
 * Inventory Stock Watcher
 */
(function() {
  function initInventoryWatchers() {
    const scriptTags = document.querySelectorAll('script[id^="inventory-map-"]');
    scriptTags.forEach(function(scriptTag) {
      if (scriptTag.hasAttribute('data-initialized')) return;
      scriptTag.setAttribute('data-initialized', 'true');

      try {
        const inventoryMap = JSON.parse(scriptTag.textContent);
        const optionId = scriptTag.id.replace('inventory-map-', '');
        const stockSpan = document.getElementById(optionId + '-stock');
        
        if (!stockSpan) return;
        const picker = stockSpan.closest('variant-picker');
        if (!picker) return;

        function updateStockText(variantId) {
          const vData = inventoryMap[variantId];
          if (vData && vData.manage && vData.qty > 0 && vData.qty < 10) {
            stockSpan.textContent = "Only " + vData.qty + " Left!";
          } else {
            stockSpan.textContent = "";
          }
        }

        const initialVariantInput = picker.querySelector('input[name="id"]');
        let lastId = initialVariantInput ? initialVariantInput.value : null;

        setInterval(function() {
          const currentInput = picker.querySelector('input[name="id"]');
          if (currentInput && currentInput.value !== lastId) {
            lastId = currentInput.value;
            updateStockText(lastId);
          }
        }, 250);
      } catch (e) {
        console.error('Error initializing inventory watcher:', e);
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInventoryWatchers);
  } else {
    initInventoryWatchers();
  }

  // Also observe for dynamic content changes (e.g., Quick view loaded)
  const observer = new MutationObserver(function(mutations) {
    let shouldInit = false;
    for (let i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length > 0) {
        shouldInit = true;
        break;
      }
    }
    if (shouldInit) {
      initInventoryWatchers();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
/* Product Offers Copy Functionality */
document.addEventListener("DOMContentLoaded", function() {
  function initCopyButtons() {
    const copyBtns = document.querySelectorAll(".product-offers__btn:not(.initialized)");
    
    copyBtns.forEach(function(copyBtn) {
      copyBtn.classList.add("initialized");
      const originalContent = copyBtn.innerHTML;
      let isCopied = false;

      copyBtn.addEventListener("click", function(e) {
        e.preventDefault();
        if (isCopied) return;
        
        const code = copyBtn.getAttribute("data-code");
        if (!code) return;

        navigator.clipboard.writeText(code).then(() => {
          isCopied = true;
          
          const currentWidth = copyBtn.getBoundingClientRect().width;
          copyBtn.style.width = currentWidth + "px";
          
          copyBtn.innerHTML = `
            <span class="product-offers__btn-text">Code Copied</span>
            <svg class="tick-icon" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          `;
          
          copyBtn.style.transform = "scale(0.95)";
          setTimeout(() => { copyBtn.style.transform = "scale(1)"; }, 150);
          
          setTimeout(function() {
            copyBtn.innerHTML = originalContent;
            copyBtn.style.width = "auto";
            isCopied = false;
          }, 2500);
        });
      });
    });
  }

  // Force Oswald font-family on Whatmore App paragraph elements overriding inline style !important
  function overrideWhatmoreFont() {
    document.querySelectorAll('.whatmore-carousel-events > div:first-child > p').forEach(function(el) {
      if (el.style.getPropertyValue('font-family') !== "'Oswald', sans-serif") {
        el.style.setProperty('font-family', "'Oswald', sans-serif", 'important');
      }
    });
  }

  // Initialize on load
  initCopyButtons();
  overrideWhatmoreFont();
  
  // Re-initialize if DOM changes (e.g., dynamic rendering or Quick View)
  const observer = new MutationObserver(function() {
    initCopyButtons();
    overrideWhatmoreFont();
  });
  observer.observe(document.body, { childList: true, subtree: true });
});

/**
 * ClickPost EDD Pincode Checker - Enforce numbers-only input
 */
document.addEventListener('DOMContentLoaded', function() {
  function sanitizeInput(input) {
    const original = input.value;
    const sanitized = original.replace(/\D/g, '').slice(0, 6);
    if (original !== sanitized) {
      input.value = sanitized;
    }
  }

  // Prevent non-digit keystrokes (allow navigation & shortcut keys)
  document.addEventListener('keydown', function(e) {
    const target = e.target;
    if (target && target.matches('.clickpost-edd__drop_pincode-input, input[name="drop_pincode"]')) {
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'Escape' ||
        e.key === 'Enter' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.ctrlKey ||
        e.metaKey
      ) {
        return;
      }
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }
  }, true);

  // Sanitize input & paste events
  document.addEventListener('input', function(e) {
    const target = e.target;
    if (target && target.matches('.clickpost-edd__drop_pincode-input, input[name="drop_pincode"]')) {
      sanitizeInput(target);
    }
  }, true);

  // Set numeric keypad attributes on focus (zero performance cost, no MutationObserver)
  document.addEventListener('focusin', function(e) {
    const target = e.target;
    if (target && target.matches('.clickpost-edd__drop_pincode-input, input[name="drop_pincode"]')) {
      if (target.getAttribute('inputmode') !== 'numeric') {
        target.setAttribute('inputmode', 'numeric');
        target.setAttribute('pattern', '[0-9]*');
      }
    }
  }, true);
});
